import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { ICatalogReducerState } from '../../../redux/reducers/catalogReducer';
import { ICatalogVacanciesReducerState } from '../../../redux/reducers/catalogVacanciesReducer';
import { RootState } from '../../../redux/reducers/rootReducer';
import { CATEGORIES_WITH_NAME_AS_KEY } from '../../../utils/constants/categories-with-name-as-key';
import {
  IFilterSelectsUI,
  IFilterSelectsUIData,
} from '../../../utils/constants/filter-selects';
import { useChangeFilters } from '../../../utils/useChangeFilters';
import { Tag } from '../../UI/Tag';
import classes from './VacanciesCardHeader.module.scss';

function VacanciesCardHeader() {
  const [searchParams] = useSearchParams();
  const pagination = useSelector<
    RootState,
    ICatalogVacanciesReducerState['pagination'] | null
  >((state) => state.catalogVacanciesReducer.pagination);
  const [filters, setFilters] = useState<null | IFilterSelectsUI[]>();

  const { changeFilter, clearAllFilterCategory } = useChangeFilters({
    filters,
  });

  const res = useSelector<RootState, ICatalogReducerState['filters'] | []>(
    (state) => state.catalogReducer.filters,
  );

  const isShowClear = useMemo(
    () => filters?.some((itemFilter) => itemFilter.data.some((itemData) => itemData.isSelected)),
    [filters],
  );
  const selectedFilters = useMemo(() => {
    const result = filters?.map((filer) => ({
      category: filer.category,
      data: filer.data.filter((item) => item.isSelected),
    }));

    return result?.flat();
  }, [filters]);

  const changedFilter = (
    item: IFilterSelectsUIData,
    category: string,
  ): void => {
    setFilters(changeFilter(item, category, false));
  };

  useEffect(() => {
    if (res.length > 0) {
      // Получаем параметры из фильтров и строки запроса
      const getParams = res?.map((filter) => ({
        category: filter.category,
        data: searchParams
          .getAll(filter.category)
          .flatMap((data) => data.split(',')),
      }));

      const newFilter = res?.map((item) => {
        const currentParams = getParams?.find(
          (filter) => filter.category === item.category,
        );
        const isNameAsKey = CATEGORIES_WITH_NAME_AS_KEY.includes(item.category);

        // Обновляем данные для каждого фильтра
        const updatedData = item.data.map((itemData) => ({
          ...itemData,
          isSelected:
            currentParams?.data.includes(
              isNameAsKey ? itemData.name : itemData.id,
            ) || false,
        }));

        return {
          ...item,
          data: updatedData,
        };
      });

      setFilters(newFilter as IFilterSelectsUI[]);
    }
  }, [res]);

  const isLoading = useSelector<
    RootState,
    ICatalogVacanciesReducerState['isLoading'] | null
  >((state) => state.catalogVacanciesReducer.isLoading);

  const clearAllCategories = () => {
    setFilters(clearAllFilterCategory());
  };

  return (
    <div className={classes['vacancies-card-header']}>
      <div className={classes['vacancies-card-header__title']}>
        <div>
          {isLoading ? (
            'Поиск вакансий'
          ) : (
            <>
              Вакансий найдено:
              {' '}
              <span className={classes['vacancies-card-header__counter']}>
                {pagination?.data.totalCount || 0}
              </span>
            </>
          )}
        </div>
      </div>

      {isShowClear && (
        <div className={classes.tags}>
          <div className={classes['tag-container']}>
            {selectedFilters?.map((filter) => filter.data.map((data) => (
              <Tag
                key={data.id}
                className={classes.tag}
                onClose={() => changedFilter(data, filter.category)}
              >
                {data.displayName || data.name || ''}
              </Tag>
            )))}
            <Tag
              key="clear"
              className={classes.tag}
              onClose={clearAllCategories}
            >
              Очистить фильтры
            </Tag>
          </div>
        </div>
      )}
    </div>
  );
}

export default VacanciesCardHeader;
